import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Tooltip } from "antd";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import CTable from "components/CTable";
import useFilter from "components/Filter";
import {
  getGridify,
  getStatus,
  showConfirm,
  showError,
  useNewState,
} from "helpers/utility";
import useAjax from "helpers/ajax";
import Toolbar from "components/Toolbar";
import { DEFAULT_PAGE_SIZE } from "constants";
import { useAuth } from "helpers/auth";

const Companies = () => {
  const {
    user: { companyID },
  } = useAuth();

  const { get, del } = useAjax();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useNewState({
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const onDelete = (record) => {
    showConfirm("Data once deleted can not be restored.").then(() => {
      del("api/v1/companies", { key: record.companyID })
        .then(() => {
          const pager = { ...pagination, current: 1 };
          loadData(pager);
        })
        .catch((err) => {
          showError(err);
        });
    });
  };

  const columns = [
    {
      title: "Company name",
      dataIndex: "companyName",
      sorter: true,
      ...useFilter(),
    },
    {
      title: "Contact no.",
      dataIndex: "contactNo",
      sorter: true,
      ...useFilter(),
    },
    {
      title: "Email address",
      dataIndex: "emailAddress",
      sorter: true,
      ...useFilter(),
    },
    {
      title: "Company address",
      dataIndex: "companyAddress",
      ...useFilter(),
    },
    {
      title: "Company type",
      dataIndex: "companyTypeName",
      sorter: true,
      ...useFilter(),
    },
    {
      title: "Status",
      dataIndex: "active",
      width: "100px",
      sorter: true,
      ...useFilter(),
      render: (text) => getStatus(text),
    },
    {
      title: "Actions",
      width: "65px",
      align: "center",
      render: (text, record) => (
        <Button.Group>
          <Tooltip placement="topLeft" title="Edit">
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                navigate(`/admin/companies/edit/${record.companyID}`, {
                  state: record,
                });
              }}
              disabled={
                !(
                  record.companyID === companyID ||
                  record.parentCompanyID === companyID
                )
              }
            />
          </Tooltip>
          <Tooltip placement="topLeft" title="Delete">
            <Button
              type="danger"
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => onDelete(record)}
              disabled={
                record.companyID === companyID ||
                record.parentCompanyID !== companyID
              }
            />
          </Tooltip>
        </Button.Group>
      ),
    },
  ];

  const loadData = (pager, filters, sorter) => {
    const gridify = getGridify(pager, filters, sorter, [
      {
        field: "createdOn",
        order: "descend",
      },
    ]);
    get("api/v1/companies", { ...gridify })
      .then((resp) => {
        setData(resp.data);
        setPagination({
          ...pager,
          total: resp.count,
        });
      })
      .catch((err) => {
        showError(err);
      })
      .finally(() => setLoading(false));
  };

  const onTableChange = (pager, filters, sorter) => {
    loadData(pager, filters, sorter);
  };

  useEffect(() => {
    loadData(pagination);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <Toolbar>
        <Button
          type="primary"
          onClick={() => {
            navigate("/admin/companies/create");
          }}
        >
          Create
        </Button>
      </Toolbar>
      <CTable
        rowKey="companyID"
        columns={columns}
        dataSource={data}
        pagination={pagination}
        loading={loading}
        onChange={onTableChange}
      />
    </div>
  );
};
export default Companies;
